import { createSignal, For, Show } from "solid-js";
import Modal from "./Modal";
import Text from "./Text";
import { useToast } from "./MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./ItemInfoView.module.css";

export const ITEM_DETAILS_FLAG = "ITEM_DETAILS";

export interface ItemInfoPayload {
  itemId: number;
  count: number;
  damage: number;
  identifier?: string;
  name?: string;
  displayName: string;
  snbt?: string;
}

const [info, setInfo] = createSignal<ItemInfoPayload | null>(null);

export function showItemInfo(payload: ItemInfoPayload): void {
  setInfo({ ...payload });
}

function formatSnbt(input: string): string {
  let out = "";
  let indent = 0;
  let inString = false;
  let quote = "";
  const pad = () => "  ".repeat(indent);

  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (inString) {
      out += c;
      if (c === quote && input[i - 1] !== "\\") inString = false;
      continue;
    }
    if (c === '"' || c === "'") {
      inString = true;
      quote = c;
      out += c;
    } else if (c === "{" || c === "[") {
      indent++;
      out += c + "\n" + pad();
    } else if (c === "}" || c === "]") {
      indent = Math.max(0, indent - 1);
      out += "\n" + pad() + c;
    } else if (c === ",") {
      out += c + "\n" + pad();
    } else {
      out += c;
    }
  }
  return out;
}

export function ItemInfoHost() {
  const toast = useToast();
  const { t } = useTranslation();

  const rows = () => {
    const v = info();
    if (!v) return [];
    return [
      { key: "ITEM_INFO_ID", value: String(v.itemId) },
      { key: "ITEM_INFO_IDENTIFIER", value: v.identifier ?? "-" },
      { key: "ITEM_INFO_NAME", value: v.displayName },
      { key: "ITEM_INFO_COUNT", value: String(v.count) },
      { key: "ITEM_INFO_DAMAGE", value: String(v.damage) },
    ];
  };

  const formattedSnbt = () => {
    const raw = info()?.snbt;
    return raw && raw.length > 0 ? formatSnbt(raw) : null;
  };

  const copyNbt = () => {
    const raw = info()?.snbt;
    if (!raw) return;
    navigator.clipboard?.writeText(raw);
    toast?.info(t("ITEM_INFO_NBT_COPIED"));
  };

  return (
    <Show when={info()}>
      <Modal
        open={true}
        onClose={() => setInfo(null)}
        maxWidth="40rem"
        title={<Text val="ITEM_INFO_TITLE" />}
      >
        <div class={styles.body}>
          <div class={styles.fields}>
            <For each={rows()}>
              {(row) => (
                <div class={styles.field}>
                  <span class={styles.label}><Text val={row.key} /></span>
                  <span class={styles.value}>{row.value}</span>
                </div>
              )}
            </For>
          </div>

          <div class={styles.nbtHeader}>
            <span class={styles.label}><Text val="ITEM_INFO_NBT" /></span>
            <Show when={formattedSnbt()}>
              <button type="button" class="btn btn-secondary btn-sm" onClick={copyNbt}>
                <i class="fa-solid fa-copy" /> <Text val="ITEM_INFO_COPY_NBT" />
              </button>
            </Show>
          </div>
          <Show
            when={formattedSnbt()}
            fallback={<div class={styles.empty}><Text val="ITEM_INFO_NO_NBT" /></div>}
          >
            {(snbt) => <pre class={styles.nbt}>{snbt()}</pre>}
          </Show>
        </div>
      </Modal>
    </Show>
  );
}
